import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getActivitySessions, createActivitySession, endActivitySession, abortActivitySession, getActivityLogs, createActivityLog, extractErrorMessage } from '../services/api';
import { FaPlus, FaBolt, FaStop, FaTimes, FaHistory } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

const ACTIVITY_TYPES = [
  { value: 'cbt', label: 'CBT' },
  { value: 'clinic', label: 'Klinik' },
  { value: 'pain', label: 'Schmerz' },
  { value: 'humiliation', label: 'Erniedrigung' },
  { value: 'estim', label: 'E-Stim' },
  { value: 'sounding', label: 'Sounding' },
  { value: 'stretch', label: 'Dehnung' },
  { value: 'wax', label: 'Wachs' },
  { value: 'figging', label: 'Figging' },
  { value: 'breath', label: 'Atemkontrolle' },
  { value: 'bondage', label: 'Bondage' },
  { value: 'pet', label: 'Pet Play' },
  { value: 'predicament', label: 'Predicament' }
];

function ActivitySessions({ user }) {
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sessions');
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [savingLog, setSavingLog] = useState(false);
  const [formErrorSession, setFormErrorSession] = useState(null);
  const [formErrorLog, setFormErrorLog] = useState(null);
  const [newSession, setNewSession] = useState({
    user_id: '',
    type: 'cbt',
    intensity: 5,
    duration_planned: '',
    notes: '',
    tools: ''
  });
  const [newLog, setNewLog] = useState({
    user_id: '',
    session_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionsData, logsData] = await Promise.all([
        getActivitySessions(),
        getActivityLogs().catch(() => [])
      ]);
      setSessions(sessionsData.sessions || sessionsData || []);
      setLogs(logsData.logs || logsData || []);
    } catch (error) {
      console.error('Error fetching activity data:', error);
      toast.error(extractErrorMessage(error, 'Aktivitätsdaten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (savingSession) return;
    setSavingSession(true);
    setFormErrorSession(null);
    try {
      await createActivitySession({
        ...newSession,
        intensity: parseInt(newSession.intensity),
        duration_planned: newSession.duration_planned ? parseInt(newSession.duration_planned) : null
      });
      setNewSession({ user_id: '', type: 'cbt', intensity: 5, duration_planned: '', notes: '', tools: '' });
      setShowSessionModal(false);
      toast.success('Sitzung gestartet');
      fetchData();
    } catch (error) {
      console.error('Error creating session:', error);
      setFormErrorSession(extractErrorMessage(error, 'Fehler beim Erstellen der Sitzung'));
    } finally {
      setSavingSession(false);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    if (savingLog) return;
    setSavingLog(true);
    setFormErrorLog(null);
    try {
      await createActivityLog(newLog);
      setNewLog({ user_id: '', session_id: '', notes: '' });
      setShowLogModal(false);
      toast.success('Log-Eintrag erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating log:', error);
      setFormErrorLog(extractErrorMessage(error, 'Fehler beim Erstellen des Eintrags'));
    } finally {
      setSavingLog(false);
    }
  };

  const handleEndSession = async (id) => {
    if (!window.confirm('Sitzung beenden?')) return;
    try {
      await endActivitySession(id);
      toast.success('Sitzung beendet');
      fetchData();
    } catch (error) {
      console.error('Error ending session:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Beenden der Sitzung'));
    }
  };

  const handleAbortSession = async (id) => {
    if (!window.confirm('Sitzung abbrechen?')) return;
    try {
      await abortActivitySession(id);
      toast.success('Sitzung abgebrochen');
      fetchData();
    } catch (error) {
      console.error('Error aborting session:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Abbrechen der Sitzung'));
    }
  };

  const closeSessionModal = () => {
    if (savingSession) return;
    setShowSessionModal(false);
    setFormErrorSession(null);
  };

  const closeLogModal = () => {
    if (savingLog) return;
    setShowLogModal(false);
    setFormErrorLog(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { backgroundColor: '#43b581', label: 'Aktiv' },
      completed: { backgroundColor: '#7289da', label: 'Abgeschlossen' },
      aborted: { backgroundColor: '#f04747', label: 'Abgebrochen' }
    };
    const s = styles[status] || { backgroundColor: '#72767d', label: status || 'Unbekannt' };
    return <span className="badge" style={{ backgroundColor: s.backgroundColor }}>{s.label}</span>;
  };

  const getTypeLabel = (type) => {
    const found = ACTIVITY_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getIntensityColor = (intensity) => {
    if (intensity >= 8) return '#f04747';
    if (intensity >= 5) return '#faa61a';
    return '#43b581';
  };

  const activeSessions = sessions.filter(s => s.status === 'active');
  const completedSessions = sessions.filter(s => s.status !== 'active');

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaBolt /> Aktivitätssitzungen</h1>
          <div className="header-actions">
            <button className="add-btn" onClick={() => setShowSessionModal(true)}>
              <FaPlus /> Neue Sitzung
            </button>
            <button className="add-btn secondary" onClick={() => setShowLogModal(true)}>
              <FaPlus /> Log-Eintrag
            </button>
          </div>
        </div>

        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveTab('sessions')}>
            <FaBolt /> Sitzungen
          </button>
          <button className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
            <FaHistory /> Verlauf
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'sessions' ? (
          <div className="data-table-container">
            {activeSessions.length > 0 && (
              <>
                <h3 style={{ color: '#43b581', marginBottom: '12px' }}>Aktive Sitzungen</h3>
                <table className="data-table" style={{ marginBottom: '24px' }}>
                  <thead>
                    <tr>
                      <th>Typ</th>
                      <th>Benutzer</th>
                      <th>Intensität</th>
                      <th>Geplante Dauer</th>
                      <th>Gestartet</th>
                      <th>Tools</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSessions.map(session => (
                      <tr key={session.id}>
                        <td><span className="badge">{getTypeLabel(session.type)}</span></td>
                        <td>{session.username || session.user_id}</td>
                        <td>
                          <span style={{ color: getIntensityColor(session.intensity), fontWeight: 'bold' }}>
                            {session.intensity}/10
                          </span>
                        </td>
                        <td>{session.duration_planned ? `${session.duration_planned} Min` : '-'}</td>
                        <td>{new Date(session.created_at || session.started_at).toLocaleString('de-DE')}</td>
                        <td>{session.tools || '-'}</td>
                        <td>
                          <button className="icon-btn success" onClick={() => handleEndSession(session.id)} title="Beenden">
                            <FaStop />
                          </button>
                          <button className="icon-btn danger" onClick={() => handleAbortSession(session.id)} title="Abbrechen">
                            <FaTimes />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {completedSessions.length > 0 ? (
              <>
                <h3 style={{ color: '#b9bbbe', marginBottom: '12px' }}>Vergangene Sitzungen</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Typ</th>
                      <th>Benutzer</th>
                      <th>Intensität</th>
                      <th>Status</th>
                      <th>Dauer</th>
                      <th>Tools</th>
                      <th>Notizen</th>
                      <th>Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedSessions.map(session => (
                      <tr key={session.id}>
                        <td><span className="badge">{getTypeLabel(session.type)}</span></td>
                        <td>{session.username || session.user_id}</td>
                        <td>
                          <span style={{ color: getIntensityColor(session.intensity) }}>
                            {session.intensity}/10
                          </span>
                        </td>
                        <td>{getStatusBadge(session.status)}</td>
                        <td>{session.duration_actual ? `${session.duration_actual} Min` : session.duration_planned ? `${session.duration_planned} Min (geplant)` : '-'}</td>
                        <td>{session.tools || '-'}</td>
                        <td>{session.notes || '-'}</td>
                        <td>{new Date(session.created_at || session.started_at).toLocaleDateString('de-DE')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : activeSessions.length === 0 ? (
              <div className="no-data">Keine Sitzungen vorhanden</div>
            ) : null}
          </div>
        ) : (
          <div className="data-table-container">
            {logs.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Sitzung</th>
                    <th>Notizen</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td>{new Date(log.created_at || log.timestamp).toLocaleString('de-DE')}</td>
                      <td>{log.username || log.user_id}</td>
                      <td>{log.session_id || '-'}</td>
                      <td>{log.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Kein Verlauf vorhanden</div>
            )}
          </div>
        )}

        {showSessionModal && (
          <div className="modal-overlay" onClick={closeSessionModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Aktivitätssitzung</h2>
              {formErrorSession && <div className="shared-form-error-banner">{formErrorSession}</div>}
              <form onSubmit={handleCreateSession}>
                <div className="form-group">
                  <UserSelect
                    value={newSession.user_id}
                    onChange={id => setNewSession({ ...newSession, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Aktivitätstyp *</label>
                  <select
                    value={newSession.type}
                    onChange={e => setNewSession({ ...newSession, type: e.target.value })}
                    required
                  >
                    {ACTIVITY_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Intensität: {newSession.intensity}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newSession.intensity}
                    onChange={e => setNewSession({ ...newSession, intensity: parseInt(e.target.value) })}
                    className="slider"
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#72767d' }}>
                    <span>Leicht</span>
                    <span>Mittel</span>
                    <span>Extrem</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Geplante Dauer (Minuten)</label>
                  <input
                    type="number"
                    value={newSession.duration_planned}
                    onChange={e => setNewSession({ ...newSession, duration_planned: e.target.value })}
                    placeholder="z.B. 30"
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Tools / Hilfsmittel</label>
                  <input
                    type="text"
                    value={newSession.tools}
                    onChange={e => setNewSession({ ...newSession, tools: e.target.value })}
                    placeholder="z.B. Seile, Klemmen, ..."
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newSession.notes}
                    onChange={e => setNewSession({ ...newSession, notes: e.target.value })}
                    placeholder="Zusätzliche Notizen..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeSessionModal} disabled={savingSession}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingSession}>{savingSession ? 'Speichert\u2026' : 'Sitzung starten'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showLogModal && (
          <div className="modal-overlay" onClick={closeLogModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neuer Log-Eintrag</h2>
              {formErrorLog && <div className="shared-form-error-banner">{formErrorLog}</div>}
              <form onSubmit={handleCreateLog}>
                <div className="form-group">
                  <UserSelect
                    value={newLog.user_id}
                    onChange={id => setNewLog({ ...newLog, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Sitzungs-ID</label>
                  <select
                    value={newLog.session_id}
                    onChange={e => setNewLog({ ...newLog, session_id: e.target.value })}
                  >
                    <option value="">Keine Sitzung</option>
                    {activeSessions.map(s => (
                      <option key={s.id} value={s.id}>
                        {getTypeLabel(s.type)} - {new Date(s.created_at || s.started_at).toLocaleString('de-DE')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newLog.notes}
                    onChange={e => setNewLog({ ...newLog, notes: e.target.value })}
                    placeholder="Was ist passiert?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeLogModal} disabled={savingLog}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingLog}>{savingLog ? 'Speichert\u2026' : 'Eintragen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ActivitySessions;
